import { useState } from 'react';
import Button from '../../atoms/Button/Button';
import cn from '../../../utils/cn';
import styles from './BatchActionProgressionContainer.module.css';

/**
 * BatchActionProgressionContainer - One section of progress for a single batch action (treatment).
 * @param {object} props
 * @param {string} props.title - Action label
 * @param {string} props.description - Description (e.g. "X / Y lignes traitées")
 * @param {string[]} [props.messages] - Messages from API (success or error) to display under the action
 * @param {import('react').ReactNode} [props.action] - Optional action element rendered on the right side
 * @param {import('react').ReactNode} [props.progressIndicator] - Optional progress indicator
 * @param {string} [props.showMessagesLabel] - Label for the expand messages button
 * @param {string} [props.hideMessagesLabel] - Label for the collapse messages button
 * @returns {JSX.Element} BatchActionProgressionContainer component
 */
function BatchActionProgressionContainer({
  progressIndicator = null,
  title,
  description,
  messages = [],
  action = null,
  showMessagesLabel = 'Voir les messages',
  hideMessagesLabel = 'Masquer les messages',
}) {
  const [isMessagesExpanded, setIsMessagesExpanded] = useState(false);

  return (
    <section className={styles.section} aria-label={title}>
      {progressIndicator}
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        <p className={styles.description}>{description}</p>
        {messages.length > 0 ? (
          <>
            <Button
              size="small"
              color="no-background"
              onClick={() => setIsMessagesExpanded((prev) => !prev)}
              aria-expanded={isMessagesExpanded}
            >
              {isMessagesExpanded ? hideMessagesLabel : showMessagesLabel}
            </Button>
            <div
              className={cn([
                styles['messages-wrapper'],
                isMessagesExpanded && styles['messages-wrapper-expanded'],
              ])}
            >
              <div className={styles['messages-inner']}>
                <ul className={styles.messages} aria-live="polite">
                  {messages.map((msg, idx) => (
                    <li key={idx}>{msg}</li> // eslint-disable-line react/no-array-index-key -- append-only list
                  ))}
                </ul>
              </div>
            </div>
          </>
        ) : null}
      </div>
      {action && <div className={styles.action}>{action}</div>}
    </section>
  );
}

export default BatchActionProgressionContainer;
